import { useContext } from 'react';
import { Check, Circle, X } from 'lucide-react';
import { PASOS, resumen } from './arranque';
import { ContextoAuth } from '../lib/contextoAuth';
import Ayuda from './Ayuda';

// U-04 · El paso «Qué manejas» ofrece además pasar el inventario que ya
// existe (Excel, libreta o nada escrito) con el asistente de migración.
// Solo a quien puede abrir esa pantalla: el mismo permiso que la enseña en
// el menú (`importar`). Un enlace a una pantalla que no es tuya es una
// fachada. Se lee el contexto directo (no `useAuth()`, que lanza sin
// proveedor): sin sesión no hay enlace, y la pantalla sigue pintándose como
// siempre. Parche del carril `importador` (IMPORTADOR-U-04a2.patch.md).
const ENLACE_MIGRACION = '¿Ya llevas tu inventario en Excel o en una libreta? Pásalo a TEGO';

// EL ASISTENTE DE PRIMER ARRANQUE · criterio 4.2 del Bloque 4.
//
// Va en el Tablero, arriba, y desaparece solo cuando los tres pasos
// obligatorios están hechos. No es un modal ni un flujo que secuestra la
// pantalla: quien entra con prisa a mirar una existencia no tiene por qué
// pasar por un asistente para llegar.
//
// SE PUEDE ABANDONAR Y RETOMAR, que lo pide el criterio literalmente. Y
// no hace falta guardar nada para eso: el progreso se calcula de lo que
// la empresa ya tiene, así que «retomar» es simplemente volver a entrar.
export default function PrimerArranque({ estado, irA, onOcultar }) {
  const sesion = useContext(ContextoAuth);
  if (!estado || !estado.mostrar) return null;
  const cabecera = resumen(estado);
  const puede = sesion?.puede;
  const ofreceMigracion = typeof puede === 'function' && puede('importar');

  return (
    <section className="rounded-xl border border-[var(--color-marca)]/30
                        bg-[var(--color-marca)]/5 p-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {cabecera.titulo}
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">{cabecera.cuerpo}</p>
        </div>
        <button
          type="button"
          onClick={onOcultar}
          className="shrink-0 inline-flex items-center gap-1 text-sm
                     text-gray-500 hover:text-gray-700"
        >
          <X className="h-4 w-4" aria-hidden="true" /> No enseñarlo más
        </button>
      </header>

      {/* EL PROGRESO, VISIBLE. Es lo que separa un asistente de una lista
          de tareas: saber cuánto queda es lo que hace que se termine. */}
      <p className="mt-3 text-sm font-semibold text-gray-700">
        {estado.hechos} de {estado.de} listos
      </p>
      <div className="mt-1 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full bg-[var(--color-marca)]"
          style={{ width: `${(estado.hechos / estado.de) * 100}%` }}
        />
      </div>

      <ol className="mt-4 flex flex-col gap-3">
        {estado.pasos.map((p) => {
          const t = PASOS[p.clave];
          if (!t) return null;
          return (
            <li key={p.clave} className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0" aria-hidden="true">
                {p.hecho
                  ? <Check className="h-5 w-5 text-green-700" />
                  : <Circle className="h-5 w-5 text-gray-400" />}
              </span>
              <div className="flex-1">
                <p className={`font-semibold ${p.hecho ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                  {t.titulo}
                  {!p.obligatorio && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (solo si aplica)
                    </span>
                  )}
                </p>
                {!p.hecho && (
                  <>
                    <p className="text-sm text-gray-600">{t.que}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{t.porque}</p>
                    {/* EL BOTÓN LLEVA AL SITIO. Un asistente que dice qué
                        falta y no lleva a hacerlo deja el trabajo de
                        buscar, que es la mitad del trabajo. */}
                    <button
                      type="button"
                      onClick={() => irA(t.modulo)}
                      className="mt-1.5 text-sm font-semibold
                                 text-[var(--color-marca)]"
                    >
                      {t.boton} →
                    </button>
                    {p.clave === 'productos' && ofreceMigracion && (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                        <button
                          type="button"
                          onClick={() => irA('migracion')}
                          className="min-h-11 w-full text-left text-sm font-semibold
                                     text-[var(--color-marca)]"
                        >
                          {ENLACE_MIGRACION} →
                        </button>
                        <p className="text-sm text-gray-600">
                          Abre «Pasar mi inventario a TEGO»: tres preguntas y
                          después revisas lo que entendió antes de guardar.
                        </p>
                        <Ayuda id="migracion.primer_arranque" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
